/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'react',
	'prop-types',
	'../../../nls/StringResources',
	'@ba/ba-ui-carbon-toolkit',
	'./FormatConstants',
	'@ba/ba-graphics/dist/icons-js/information_16',
	'@ba/ba-ui-carbon'
], function(_, React, PropTypes, StringResources, Toolkit, FormatConstants, Information16, Carbon) {
	'use strict';

	const { Checkbox, Label, SVGIcon, Tooltip, FlexLayout, FlexItem } = Toolkit;
	const { Dropdown, TextInput, ComboBox } = Carbon;
	return class FormatProperties extends React.Component {
		static propTypes = {
			state: PropTypes.shape({
				local: PropTypes.bool,
				properties: PropTypes.object,
				type: PropTypes.string
			}),
			autoDescription: PropTypes.string,
			defaultDescription: PropTypes.string,
			definition: PropTypes.object,
			dialogNode: PropTypes.object,
			disabledItems: PropTypes.array,
			onChange: PropTypes.func
		};
		constructor(props) {
			super(props);
			this.definition = props.definition;
			this.propMap = {
				[FormatConstants.FORMAT_TYPES.TEXT]: [this.getMissingValuesOptions],
				[FormatConstants.FORMAT_TYPES.NUMBER]: [this.getDecimalOptions, this.getCombinedGroupingAndAbbrev, this.getNumberLabelOptions, this.getNegativeSignOptions, this.getMissingValuesOptions],
				[FormatConstants.FORMAT_TYPES.PERCENT]: [this.getDecimalOptions, this.getUseGrouping, this.getPercentOptions, this.getMissingValuesOptions],
				[FormatConstants.FORMAT_TYPES.CURRENCY]: [this.getDecimalOptions, this.getCombinedGroupingAndAbbrev, this.getCurrencyOptions, this.getNumberLabelOptions, this.getMissingValuesOptions],
				[FormatConstants.FORMAT_TYPES.DATE]: [this.getDateOptions, this.getMissingValuesOptions],
				[FormatConstants.FORMAT_TYPES.DATETIME]: [this.getDateTimeOptions, this.getMissingValuesOptions]
			};

			this.dialogNode = props.dialogNode;
			this.disabledItems = props.disabledItems;
			this.autoDescription = this.props.autoDescription;
			this.defaultDescription = this.props.defaultDescription;
		}

		onChange(spec) {
			this.props.onChange && this.props.onChange(spec);
		}

		onValueChange(spec) {
			this.onChange(spec);
		}

		static _getOfType(type, string) {
			return `${type}_${string}`;
		}

		getAutoOptions() {
			const desc = <Label label={this.autoDescription} className='propertySubgroup' type='caption'/>;
			return desc;
		}

		getDefaultOptions() {
			const desc = <Label label={this.defaultDescription} className='propertySubgroup' type='caption'/>;
			return desc;
		}

		static _getDecimalProps(properties) {
			const isDigitNull = properties.maximumFractionDigits === null || properties.minimumFractionDigits === null;
			const isDigitUndef = _.isUndefined(properties.maximumFractionDigits) || _.isUndefined(properties.minimumFractionDigits);

			// If there is not a set decimal size (upper and lower limit on fraction digits) then the user has not specifically
			// set a format, so we set the property to default
			properties.maximumFractionDigits = !isDigitNull && !isDigitUndef ? properties.maximumFractionDigits.toString() : FormatConstants.FORMAT_DEFAULT_VALUE;

			return properties;
		}

		static _shouldExtendAbbreviation(type, spec) {
			return ((type === FormatConstants.FORMAT_TYPES.NUMBER && !spec[FormatProperties._getOfType(FormatConstants.FORMAT_TYPES.CURRENCY, 'decimalFormatLength')])
				|| (type === FormatConstants.FORMAT_TYPES.CURRENCY && !spec[FormatProperties._getOfType(FormatConstants.FORMAT_TYPES.NUMBER, 'decimalFormatLength')]));
		}

		static _getDecimalFormatLengthProperty(properties, type) {
			const decimalFormatLengthForType = FormatProperties._getOfType(type, 'decimalFormatLength');

			if (_.isUndefined(properties[decimalFormatLengthForType]) && !_.isUndefined(properties.decimalFormatLength)) {
				properties[decimalFormatLengthForType] = properties.decimalFormatLength;
			}

			return properties[decimalFormatLengthForType];
		}

		static _isAbbreviated(properties, type) {
			if (type === FormatConstants.FORMAT_TYPES.NUMBER || type === FormatConstants.FORMAT_TYPES.CURRENCY) {
				return FormatProperties._getDecimalFormatLengthProperty(properties, type) === FormatConstants.FORMAT_LENGTH_SHORT;
			}
			return false;
		}

		// Attempts to get values stored in the new typed schema
		static _remapDecimalOptionsToTyped(spec, type) {
			const decimalFormatLengthForType = FormatProperties._getOfType(type, 'decimalFormatLength');
			const maximumFractionDigitsForType = FormatProperties._getOfType(type, 'maximumFractionDigits');
			const minimumFractionDigitsForType = FormatProperties._getOfType(type, 'minimumFractionDigits');
			let finalSpec = FormatProperties._getDecimalProps(spec);
			const typedSpec = _.pick(spec, decimalFormatLengthForType, maximumFractionDigitsForType, minimumFractionDigitsForType);

			if (_.keys(typedSpec).length === 0) {
				// if there is pre-existing view state we dont want to do this and then delete max fraction digits
				// there might be an undeleted maximumFractionDigits if we change the value from a previous view of a different type
				if (!spec[FormatProperties._getOfType(FormatConstants.FORMAT_TYPES.CURRENCY, 'maximumFractionDigits')] &&
				!spec[FormatProperties._getOfType(FormatConstants.FORMAT_TYPES.PERCENT, 'maximumFractionDigits')] &&
				!spec[FormatProperties._getOfType(FormatConstants.FORMAT_TYPES.NUMBER, 'maximumFractionDigits')]) {
					_.extend(finalSpec, {
						[maximumFractionDigitsForType]: spec.maximumFractionDigits ? spec.maximumFractionDigits : FormatConstants.FORMAT_DEFAULT_VALUE,
						[minimumFractionDigitsForType]: spec.minimumFractionDigits ? spec.minimumFractionDigits : FormatConstants.FORMAT_DEFAULT_VALUE
					});
				} else {
					_.extend(finalSpec, {
						[maximumFractionDigitsForType]: FormatConstants.FORMAT_DEFAULT_VALUE,
						[minimumFractionDigitsForType]: FormatConstants.FORMAT_DEFAULT_VALUE
					});
				}
				if (FormatProperties._shouldExtendAbbreviation(type, spec)) {
					_.extend(finalSpec, {
						[decimalFormatLengthForType]: spec.decimalFormatLength ? spec.decimalFormatLength : FormatConstants.FORMAT_DEFAULT_VALUE
					});
				}
			}

			return finalSpec;
		}

		/**
		 * Used to generate the format dialog view
		 * @param {*} spec data format spec, modified via formatAction
		 * @param {*} type data format type
		 */
		getDecimalOptions(spec, type) {
			const maximumFractionDigitsForType = FormatProperties._getOfType(type, 'maximumFractionDigits');
			const minimumFractionDigitsForType = FormatProperties._getOfType(type, 'minimumFractionDigits');

			// Check if we have the new schema in use, else fall back to old decimal prop schema
			let finalSpec = FormatProperties._remapDecimalOptionsToTyped(spec, type);
			// save new style props
			spec = _.extend(spec, finalSpec);
			if (!_.isUndefined(spec.maximumFractionDigits)) {
				delete spec.maximumFractionDigits;
			}
			const selected = this.definition.decimalSize.options.find(current => spec[maximumFractionDigitsForType] === current.value);
			const decimalSize = <FlexLayout className='propertyGroup'><Dropdown
				titleText={this.definition.decimalSize.label}
				className='fullWidth'
				disabled={FormatProperties._isAbbreviated(spec, type)}
				selectedItem={selected ? selected.label : this.definition.decimalSize.options[0].label}
				items={this.definition.decimalSize.options}
				onChange={function(v) {
					this.onChange({ [maximumFractionDigitsForType]: v.selectedItem.label, [minimumFractionDigitsForType]: v.selectedItem.label });
				}.bind(this)}
				light
				domNodeToAttachTo={this.dialogNode}
			/></FlexLayout>;

			return decimalSize;
		}

		getUseGrouping(properties, type){
			const useGroupingForType =  FormatProperties._getOfType(type, 'useGrouping');

			if (!_.isUndefined(properties.useGrouping)) {
				properties[useGroupingForType] = properties.useGrouping;
				delete properties.useGrouping;
			}

			const isNumAbbrev = FormatProperties._isAbbreviated(properties, type);
			properties[useGroupingForType] = _.isUndefined(properties[useGroupingForType]) || properties[useGroupingForType];

			const useGrouping = <FlexLayout>
				<Checkbox
					label={StringResources.get('formatUseGrouping')}
					disabled={isNumAbbrev}
					checked={!isNumAbbrev && properties[useGroupingForType]}
					onChange={function(v) {
						let data = {};
						data[useGroupingForType] = v;
						this.onChange(data);
					}.bind(this)}
					className = {!isNumAbbrev && properties[useGroupingForType] ? 'is-checked' : ''}
				/></FlexLayout>;

			return useGrouping;
		}

		getCombinedGroupingAndAbbrev(properties, type) {
			const combined = <FlexLayout className='propertySubgroup' justifyContent='space-between' direction='column'>
				<FlexItem>
					{this.getUseGrouping(properties, type)}
				</FlexItem>
				<FlexItem>
					{this.getAbbreviateOptions(properties, type)}
				</FlexItem>
			</FlexLayout>;
			return combined;
		}

		getCurrencyOptions(properties) {
			properties.currencyCode = properties.currencyCode || FormatConstants.FORMAT_DEFAULT_VALUE;

			const selectedCurrencyCode = this.definition.currencyCode.options.find(current => properties.currencyCode === current.value);
			const currencyCode = <Dropdown
				selectedItem={ selectedCurrencyCode ? selectedCurrencyCode.label : this.definition.currencyCode.options[0].label }
				className='halfWidth'
				items={this.definition.currencyCode.options}
				titleText={<div className='infoLabel'>{this.definition.currencyCode.label}</div>}
				onChange={(v) => this.onChange({ currencyCode: v.selectedItem.value })}
				light
				domNodeToAttachTo={this.dialogNode}
			/>;

			// Options from modelling have code === 'true', symbol === 'false'
			if (properties.currencyDisplay === 'code') {
				properties.currencyDisplay = 'true';
			} else if (properties.currencyDisplay === 'symbol') {
				properties.currencyDisplay = 'false';
			} else if (!properties.currencyDisplay) {
				properties.currencyDisplay = FormatConstants.FORMAT_DEFAULT_VALUE;
			}

			const id = 'currencySymbol';
			const currencySymbol = <div className='propertySubgroup'>
				<TextInput
					id={id}
					value={properties.currencySymbol}
					light
					labelText={<FlexLayout direction='row'>
						<div className='infoLabel'>{StringResources.get('currencySymbolLabel')}</div>
						<Tooltip
							title={StringResources.get('customSymbolLabelInfo')}>
							<SVGIcon
								iconId={Information16.default.id}
								verticalAlign='top'
								className='customVisInfoIcon' />
						</Tooltip>
					</FlexLayout>}
					onChange={event => {
						let v = event.currentTarget.value;
						// want to clear the setting if passed an empty string here
						if (v === '') {
							v = undefined;
						}
						this.onValueChange({ currencySymbol: v });
					}}
					className='halfWidth'
				/>
			</div>;

			const selected = this.definition.useIntlSymbol.options.find(current => properties.currencyDisplay === current.value);
			const currencyDisplay = (
				<div className='propertyGroup'>
					<Dropdown
						titleText={this.definition.useIntlSymbol.label}
						selectedItem={selected ? selected.label : this.definition.useIntlSymbol.options[0].label}
						items={this.definition.useIntlSymbol.options}
						className='fullWidth'
						onChange={function(v) {
							this.onChange({ currencyDisplay: v.selectedItem.value });
						}.bind(this)}
						light
						domNodeToAttachTo={this.dialogNode}
					/>
				</div>
			);
			const currency = <FlexLayout alignItems='baseline' className='propertyGroup' justifyContent='space-between'>
				{currencyCode}
				{currencySymbol}
			</FlexLayout>;

			return [currency, currencyDisplay];
		}

		getNumberLabelOptions(properties) {
			const id = 'numberLabel';
			const numberLabel = StringResources.get('numberLabel');
			const defaultLabel = StringResources.get('default');
			const beginningLabel = StringResources.get('beginning');
			const endLabel = StringResources.get('end');
			const numberLabelPositionOptions = [
				{
					value: 'default',
					label: defaultLabel,
					title: defaultLabel,
					'aria-label': defaultLabel
				},
				{
					value: 'left',
					label: beginningLabel,
					title: beginningLabel,
					'aria-label': beginningLabel
				},
				{
					value: 'right',
					label: endLabel,
					title: endLabel,
					'aria-label': endLabel
				}
			];

			const selected = numberLabelPositionOptions.find(current => properties.numberLabelPosition === current.value);
			const numberLabelDisplay = <FlexLayout alignItems='baseline' justifyContent='space-between' className='propertyGroup'>
				<TextInput
					id={id}
					value={properties.numberLabel}
					light
					labelText={
						<FlexLayout direction='row'>
							<div className='infoLabel'>{numberLabel}</div>
							<Tooltip
								title={StringResources.get('numberLabelInfo')}>
								<SVGIcon
									iconId={Information16.default.id}
									verticalAlign='top'
									className='customVisInfoIcon' />
							</Tooltip>
						</FlexLayout>
					}
					onChange={event => {
						let v = event.currentTarget.value;
						// want to clear the setting if passed an empty string here
						if (v === '') {
							v = undefined;
						}
						this.onValueChange({ numberLabel: v });
					}}
					className='halfWidth'
				/>
				<Dropdown
					selectedItem={selected ? selected.label : numberLabelPositionOptions[0].label}
					items={numberLabelPositionOptions}
					className='halfWidth'
					titleText={<div className='infoLabel'>{StringResources.get('numberPositionLabel')}</div>}
					onChange={(v) => this.onChange({ numberLabelPosition: v.selectedItem.value })}
					light
					domNodeToAttachTo={this.dialogNode}
				/>
			</FlexLayout>;
			return numberLabelDisplay;
		}

		getNegativeSignOptions(properties) {
			const defaultLabel = StringResources.get('default');
			const beginningLabel = StringResources.get('beginning');
			const endLabel = StringResources.get('end');
			const useTrailingSignOptions = [
				{
					'label': defaultLabel,
					'title': defaultLabel,
					'aria-label': defaultLabel
				},
				{
					'value': 'false',
					'label': beginningLabel,
					'title': beginningLabel,
					'aria-label': beginningLabel
				},
				{
					'value': 'true',
					'label': endLabel,
					'title': endLabel,
					'aria-label': endLabel
				}
			];

			//TODO: Possibly MUI and dashboard should share default list
			const signItems = ['-','()','△', '▲','▽','▼'];

			let selectedItem = null;
			if(properties.sign){
				if(!signItems.includes(properties.sign)){
					signItems.push(properties.sign);
				}
				selectedItem = properties.sign;
			}

			const selectedIndex = useTrailingSignOptions.findIndex(item => item.value === properties.useTrailingSign);

			const numberLabelDisplay = <FlexLayout alignItems='baseline' justifyContent='space-between' className='propertyGroup'>
				<ComboBox
					items={signItems}
					selectedItem={selectedItem}
					placeholder={defaultLabel}
					light
					titleText={<div className='infoLabel'>{StringResources.get('negativeSign')}</div>}
					onInputChange={value => properties.sign !== value && this.onValueChange({ sign: value })}
					shouldFilterItem={event => event.item.startsWith(event.inputValue)}
					className='halfWidth'
					direction='top'
				/>
				<Dropdown
					selectedItem={useTrailingSignOptions[selectedIndex].label}
					items={useTrailingSignOptions}
					className='halfWidth'
					titleText={<div className='infoLabel'>{StringResources.get('negativeSignPosition')}</div>}
					onChange={(v) => this.onChange({ useTrailingSign: v.selectedItem.value })}
					light
				/>
			</FlexLayout>;
			return numberLabelDisplay;
		}

		getPercentOptions(properties) {
			// Set convert percent true by default (multiply value by 100 and add the % symbol, as we have in the past)
			properties.convertPercent = properties.convertPercent === undefined ? true : properties.convertPercent;
			const convertPercent = <FlexLayout>
				<Checkbox
					label={StringResources.get('formatConvertPercent')}
					checked={properties.convertPercent}
					onChange={function(v) {
						this.onChange({ convertPercent: v });
					}.bind(this)}
					className={properties.convertPercent ? 'is-checked' : ''}
				/></FlexLayout>;
			return convertPercent;
		}

		getAbbreviateOptions(properties, type) {
			const decimalFormatLengthForType = FormatProperties._getOfType(type, 'decimalFormatLength');
			const decimalFormatLength = FormatProperties._getDecimalFormatLengthProperty(properties, type);

			const abbrevLabel = 'formatAbbreviate';
			const isPropDisabled = this.disabledItems.includes(abbrevLabel);
			const abbrev = <FlexLayout><Checkbox
				label={StringResources.get(abbrevLabel)}
				disabled={isPropDisabled}
				checked={decimalFormatLength === FormatConstants.FORMAT_LENGTH_SHORT}
				className={decimalFormatLength === FormatConstants.FORMAT_LENGTH_SHORT ? 'is-checked' : ''}
				onChange={function(v) {
					this.onChange({
						[decimalFormatLengthForType]: v ? FormatConstants.FORMAT_LENGTH_SHORT : null
					});
				}.bind(this)}
			/></FlexLayout>;
			return abbrev;
		}

		getDateOptions(properties) {
			properties.dateStyle = properties.dateStyle || FormatConstants.FORMAT_DEFAULT_VALUE;

			if (!properties.dateTimeStyle && properties.metaDataType === 'datetime') { // Initializing dateTimeStyle attribute
				properties.dateTimeStyle = FormatConstants.FORMAT_DEFAULT_VALUE;
			}
			const selected = this.definition.dateStyle.options.find(current => current.value === properties.dateStyle);
			const dateStyle = <Dropdown
				titleText={StringResources.get('dateStyle')}
				selectedItem={selected ? selected.label : this.definition.dateStyle.options[0].label}
				className='fullWidth'
				items={this.definition.dateStyle.options}
				onChange={function(v) {
					this.onChange({ dateStyle: v.selectedItem.value });
				}.bind(this)}
				light
				domNodeToAttachTo={this.dialogNode}
			/>;

			return dateStyle;
		}

		getDateTimeOptions(properties) {
			// For datetime scenario, we use dateTimeStyle attribute to preserve the user selection for Date/Time checkbox
			// and dateStyle attribute to preserve the user selection for Date checkbox.
			// So when datetime's dateStyle passes in, treat it as the dateTimeStyle value for Date/Time checkbox
			// and need reset dateStyle value to default to start with.

			if (!properties.dateTimeStyle) { // Initializing dateTimeStyle attribute
				properties.dateTimeStyle = properties.dateStyle || FormatConstants.FORMAT_DEFAULT_VALUE;
				properties.dateStyle = FormatConstants.FORMAT_DEFAULT_VALUE;
			}

			const selected = this.definition.dateStyle.options.find(current => current.value === properties.dateTimeStyle);
			const dateTimeStyle = <Dropdown
				titleText={StringResources.get('dateTimeStyle')}
				selectedItem={selected ? selected.label : this.definition.dateStyle.options[0].label}
				className='fullWidth'
				items={this.definition.dateStyle.options}
				onChange={function(v) {
					this.onChange({ dateTimeStyle: v.selectedItem.value });
				}.bind(this)}
				light
				domNodeToAttachTo={this.dialogNode}
			/>;

			return dateTimeStyle;
		}

		getMissingValuesOptions(properties) {
			const id = 'defaultValue';
			const missingValuesDisplay = <div className='propertyGroup'>
				<TextInput
					id={id}
					className='fullWidth'
					light
					value={properties.defaultValue}
					placeholder={StringResources.get('nullValueDefault')}
					labelText={<FlexLayout direction='row'>
						<div className='infoLabel'>{this.definition.whenMissing.label}</div>
						<Tooltip
							title={StringResources.get('formatMissingValueCharactersInfo')}>
							<SVGIcon
								iconId={Information16.default.id}
								verticalAlign='top'
								className='customVisInfoIcon' />
						</Tooltip>
					</FlexLayout>}
					onChange={event => {
						let v = event.currentTarget.value;
						// want to clear the setting if passed an empty string here
						if (v === '') {
							v = undefined;
						}
						this.onValueChange({ defaultValue: v });
					}}
				/>
			</div>;
			return missingValuesDisplay;
		}

		getElements() {
			let elements = [];
			if(this.props.state.local) {
				var getters = this.propMap[this.props.state.type];
				_.each(getters, (getter) => {
					elements.push(getter.call(this, this.props.state.properties, this.props.state.type));
				});
				elements.push(this.getDefaultOptions());
			} else {
				elements.push(this.getAutoOptions());
			}
			return elements;
		}

		render() {
			let properties = this.getElements();
			let className = 'formatProperties';
			if(!this.props.state.local || this.props.state.type === 'auto'){
				className += ' formatAutoProp';
			}
			return (
				<div className={className}>
					{properties}
				</div>
			);
		}
	};
	/* eslint-enable react/prop-types */
});
